import { type Dispatch, type FC, type SetStateAction, useCallback, useRef } from "react";
import { axiosPost } from "@root/shared/utils";
import { type ClientImage } from "@clientFrontend/features/publication/components/ClientImageLibraryModal/type";
import {
  isValidUploadImage,
  imageUploadErrorText,
  maxUploadableImagesErrorText,
} from "@clientFrontend/features/publication/components/ClientImageLibraryModal/uiParts/ImageUploadInfoBox/imageUploadValidator";
import { type AxiosError } from "axios";
import { Presentation } from "./Presentation";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const newrelic: any;

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    // 旧jQuery画像ライブラリを併用する画面(求人step1・会社概要など)でインスタンスが登録される。併用しない画面では未定義
    clientImageSelectModal?: {
      firstModalOpen: boolean;
    };
  }
}

type Props = {
  clientImageUrl: string;
  setClientImages: Dispatch<SetStateAction<ClientImage[]>>;
};

export const ImageUploadInfoBox: FC<Props> = ({ clientImageUrl, setClientImages }) => {
  // <input type="file">への参照を持たせる
  const fileRef = useRef<HTMLInputElement>(null);

  // 「画像アップロード」のButtonベースコンポーネントだと、<input type="file">と同様の操作ができないため、以下の処理にしている
  // 1. 「画像アップロード」のButtonベースコンポーネントにhandleClickButtonを設定
  // 2. handleClickButtonはfileRefをクリックする
  // 3. fileRefに設定してあるhandleUploadFileが実行される
  const handleClickButton = () => {
    if (fileRef.current) {
      fileRef.current.click();
    }
  };

  const uploadFiles = useCallback(
    async (files: File[]): Promise<void> => {
      await Promise.all(
        files.map(async (file) => {
          if (!isValidUploadImage(file)) {
            alert(imageUploadErrorText);
            return;
          }

          const formData = new FormData();
          formData.append("client_image[image]", file);

          await axiosPost
            .post(clientImageUrl, formData)
            .then((res) => {
              if (res.data.message) {
                alert(imageUploadErrorText);
                return;
              }

              const clientImage = res.data.data;
              const newClientImage: ClientImage = {
                id: clientImage.id,
                filename: clientImage.name,
                imageUrl: clientImage.urls.minimum,
                squareImageUrl: clientImage.urls.medium,
              };

              // アップロード完了した画像をIDの降順で並び替えしてsetClientImagesに入れる
              // 常に最新のprevClientImagesを呼び出すためにこのset関数の書き方をしている
              setClientImages((prevClientImages) => {
                const newWorkContentAlbums = [...prevClientImages, newClientImage].sort(
                  (a, b) => b.id - a.id,
                );
                return newWorkContentAlbums;
              });
            })
            .catch((error: AxiosError) => {
              if (typeof newrelic !== "undefined") {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                newrelic.noticeError(error.message);
              }
            });
        }),
      );
    },
    [clientImageUrl, setClientImages],
  );

  const uploadSequentially = useCallback(
    async (files: File[]): Promise<void> => {
      const chunkedFiles = chunkFiles(files);

      for (let i = 0; i < chunkedFiles.length; i += 1) {
        /* eslint-disable-next-line no-await-in-loop */
        await uploadFiles(chunkedFiles[i]);
      }
    },
    [uploadFiles],
  );

  // 画像アップロード機能
  const handleUploadFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;
      if (!fileList) return;

      const files = Array.from(fileList);
      if (files.length === 0) return;
      if (files.length > 25) {
        alert(maxUploadableImagesErrorText);
        return;
      }

      uploadSequentially(files);

      // FIXME:
      // JSで実装されている旧画像ライブラリでも新しくアップロードした画像が読み込みできるように、
      // firstModalOpenプロパティをfalseに変更している。正直あまり良い実装ではないのでfixしたい
      // 旧jQuery画像ライブラリを併用しない画面では window.clientImageSelectModal が未定義のため存在チェックする
      if (window.clientImageSelectModal) {
        window.clientImageSelectModal.firstModalOpen = false;
      }
    },
    [uploadSequentially],
  );

  return (
    <Presentation
      fileRef={fileRef}
      handleClickButton={handleClickButton}
      handleUploadFile={handleUploadFile}
    />
  );
};

const chunkFiles = (files: File[]): File[][] => {
  const chunkSize = 5;

  return files.reduce((acc: File[][], _, index) => {
    const isChunkStart = index % chunkSize === 0;
    if (isChunkStart) {
      const newChunk = files.slice(index, index + chunkSize);
      acc.push(newChunk);
    }
    return acc;
  }, []);
};
